/**
 * BaseModal
 * Återanvändbar modal-komponent med standardiserad struktur
 *
 * Används av: AddVehicleModal, OrganizationModals (Create/Edit/Move),
 * RouteAnalysis (vehicle maps), TripValidation (resolve/delete), DeviceImportModal, etc.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | 'full' | 'screen';
  loading?: boolean;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  full: 'max-w-full mx-4',
  screen: 'max-w-[min(80rem,calc(100vw-1.5rem))] w-full',
};

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'lg',
  loading = false,
  closeOnBackdrop = true,
  showCloseButton = true,
  className = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Hantera ESC för att stänga
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, loading]);

  // Fokusera modal vid öppning
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && !loading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      data-dq-modal="true"
    >
      {/* Backdrop – under centreringslagret */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      {/* Centreringslager – alltid viewport-bundet så höga dialoger inte klipps */}
      <div
        className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 box-border overflow-y-auto overscroll-contain"
        onClick={handleBackdropClick}
      >
        {/* Modal: header/footer fasta, body scrollar när innehållet är högre än skärmen */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`relative my-auto w-full min-w-0 ${sizeClasses[size]} ${size === 'screen' ? 'h-[min(94dvh,calc(100dvh-1rem))] max-h-[min(94dvh,calc(100dvh-1rem))]' : 'max-h-[min(92dvh,calc(100dvh-1rem))]'} flex flex-col overflow-hidden text-left transition-all transform bg-bgElevated rounded-xl shadow-xl ${className}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-bgElevated dark:bg-bgDefault shrink-0">
            <div className="min-w-0 pr-2">
              <h3 id="modal-title" className="text-lg font-semibold text-textDefault truncate">
                {title}
              </h3>
              {subtitle && (
                <p className="text-sm text-textSecondary mt-0.5">{subtitle}</p>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                disabled={loading}
                className="p-2 text-textSecondary hover:text-textDefault rounded-full hover:bg-bgHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                aria-label={t('commonUi.closeModal')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="px-4 sm:px-6 py-4 relative flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col">
            {loading && (
              <div className="absolute inset-0 bg-bgElevated/50 flex items-center justify-center z-10">
                <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
              </div>
            )}
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-wrap justify-end gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-border bg-bgElevated dark:bg-bgDefault shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Hjälpkomponenter för vanliga footer-mönster
export interface ModalFooterProps {
  onCancel: () => void;
  onSubmit?: () => void;
  cancelText?: string;
  submitText?: string;
  loading?: boolean;
  disabled?: boolean;
  submitVariant?: 'primary' | 'danger';
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  onSubmit,
  cancelText = 'Avbryt',
  submitText = 'Spara',
  loading = false,
  disabled = false,
  submitVariant = 'primary',
}) => {
  const submitClasses = submitVariant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-primary hover:bg-primary/90 text-white';

  return (
    <>
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="px-4 py-2 text-textDefault bg-bgHover rounded-lg hover:bg-bgBase transition-colors disabled:opacity-50"
      >
        {cancelText}
      </button>
      {onSubmit && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={loading || disabled}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${submitClasses}`}
        >
          {loading && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
          {submitText}
        </button>
      )}
    </>
  );
};

export default BaseModal;
